import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Divider,
  TextField,
  MenuItem,
  Paper,
  Grid,
  Container,
  Fade,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  alpha,
  Badge,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AlarmAddIcon from '@mui/icons-material/AlarmAdd';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { useForm, Controller } from 'react-hook-form';
import { Scanner } from '@yudiel/react-qr-scanner';
import CalOthour from 'utils/functions/CalOthour';
import getOtRateByDate from 'utils/functions/getOtRateByDate';
import dayjs from 'dayjs';
import * as actions from '../../redux/actions';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import 'dayjs/locale/th';

dayjs.locale('th');

export default function ManpowerAddOtRequest() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const timestamp = useSelector((state) => state.timestamp);
  const project = useSelector((state) => state.project);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const employee = useSelector((state) => state.employee);
  const holiday = useSelector((state) => state.holiday);
  const { control, handleSubmit } = useForm();
  const [claimOT, setClaimOT] = useState(false);
  const [date, setDate] = useState(dayjs());
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [openEmployeeModal, setOpenEmployeeModal] = useState(false);
  const [success, setSuccess] = useState(false);
  const [currentTime, setCurrentTime] = useState(
    new Date().toLocaleTimeString(),
  );
  // step 1: ระบุผู้ทำรายการ
  const [requester, setRequester] = useState(null);
  const [emInput, setEmInput] = useState('');
  const [scannerReady, setScannerReady] = useState(false);
  const [openScan, setOpenScan] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString());
    }, 1000);
    const scannerTimer = setTimeout(() => setScannerReady(true), 1000);
    return () => { clearInterval(timer); clearTimeout(scannerTimer); };
  }, []);

  const handleScan = async (data) => {
    if (data) {
      await dispatch(actions.employeeTimestamp({ em: data }));
      setOpenScan(false);
    }
  };

  const handleSearch = async () => {
    if (emInput) {
      await dispatch(actions.employeeTimestamp({ em: emInput }));
      setOpenScan(false);
    }
  };

  // เมื่อ employee state อัปเดต (หลังสแกน/ค้นหา) → set requester
  useEffect(() => {
    if (employee?.rows?.length > 0 && !requester && !openScan) {
      setRequester(employee.rows[0]);
    }
  }, [employee, openScan]);

  useEffect(() => {
    dispatch(actions.projectGet(id));
    dispatch(
      actions.timestampAll({
        project_id: id,
        dateStart: dayjs(date).startOf('day').toISOString(),
        dateEnd: dayjs(date).endOf('day').toISOString(),
      }),
    );
    dispatch(actions.holidayAll({ date: dayjs(date).startOf('date') }));
  }, [date]);

  // สร้าง list พนักงานจาก timestamp (unique by employee)
  const employeeOptions = [];
  const seenIds = new Set();
  (timestamp?.rows || []).forEach((item) => {
    const empId = item.employee?._id;
    if (empId && !seenIds.has(empId)) {
      seenIds.add(empId);
      employeeOptions.push({
        _id: item._id,
        employee_id: empId,
        name: formatEmployeeThaiName(item.employee),
        salary: item.salary || {},
        checkIn: item.checkIn,
      });
    }
  });

  // time options ทุก 30 นาที
  const timeOptions = [];
  for (let h = 0; h < 24; h++) {
    timeOptions.push(`${h.toString().padStart(2, '0')}:00`);
    timeOptions.push(`${h.toString().padStart(2, '0')}:30`);
  }
  timeOptions.push('23:59');

  const toDayjsTime = (str) => {
    const [hour, minute] = str.split(':');
    return dayjs().hour(Number(hour)).minute(Number(minute)).second(0);
  };

  const onSubmit = async (data) => {
    if (!selectedEmployees.length) {
      showAlert('กรุณาเลือกพนักงาน');
      return;
    }
    const confirm = await confirmAsync('ต้องการส่งคำขอ OT ใช่หรือไม่?');
    if (!confirm) return;

    try {
      const h = await CalOthour(data.startTime, data.endTime);
      const dataSubmit = {
        ...data,
        project: id,
        timestamp: selectedEmployees,
        status_claim: claimOT,
        rate: getOtRateByDate(date, holiday?.total),
        total_hours: h.toFixed(1),
        resquester: requester?._id,
        requester_data: requester,
      };
      await dispatch(actions.otRequestOrderCreatePublic(dataSubmit));
      setSuccess(true);
    } catch (e) {
      const msg = e?.response?.data?.error?.message || e?.message || 'เกิดข้อผิดพลาด';
      showAlert(msg, { status: e?.response?.status });
    }
  };

  // Step 1: ระบุผู้ทำรายการ
  if (!requester) {
    return (
      <Box className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100">
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Fade in={true}>
            <Paper
              elevation={4}
              sx={{
                p: 3,
                borderRadius: 4,
                background: 'linear-gradient(to bottom, #ffffff, #f5f8ff)',
                boxShadow: '0 8px 24px rgba(149, 157, 165, 0.2)',
              }}
            >
              <Typography variant="h4" className="font-bold text-center mb-2" sx={{ color: '#1976d2' }}>
                ขอทำโอที
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box className="flex justify-center mb-2">
                <Avatar sx={{ bgcolor: '#e3f2fd', width: 56, height: 56 }}>
                  <AlarmAddIcon fontSize="large" color="primary" />
                </Avatar>
              </Box>
              <Typography variant="h6" className="text-center mb-1" sx={{ color: '#1976d2' }}>
                โครงการ {project?.name}
              </Typography>
              <Typography variant="h5" className="text-center mb-4 font-bold" sx={{ color: '#333' }}>
                {currentTime}
              </Typography>

              {openScan && (
                <Fade in={true}>
                  <Box>
                    <Box className="flex justify-center items-center my-2">
                      <QrCodeScannerIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">สแกน QR code ผู้ทำรายการ</Typography>
                    </Box>

                    {/* QR Scanner */}
                    <Box className="flex justify-center my-4">
                      <Paper elevation={3} sx={{ p: 1, borderRadius: 2, width: { xs: 280, sm: 320 }, overflow: 'hidden' }}>
                        <Fade in={scannerReady}>
                          <Box>
                            {scannerReady && (
                              <Scanner
                                onScan={(results) => handleScan(results?.[0]?.rawValue)}
                                allowMultiple
                                containerStyle={{ width: '100%', height: '100%', paddingTop: '5px' }}
                              />
                            )}
                          </Box>
                        </Fade>
                      </Paper>
                    </Box>

                    {/* Manual input */}
                    <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>หรือค้นหาด้วยรหัสพนักงาน</Typography>
                      <Box className="flex gap-2">
                        <TextField
                          variant="outlined"
                          label="ชื่อ/รหัสพนักงาน"
                          size="small"
                          value={emInput}
                          onChange={(e) => setEmInput(e.target.value)}
                          fullWidth
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        <Button
                          variant="contained"
                          onClick={handleSearch}
                          sx={{ borderRadius: 2 }}
                        >
                          ค้นหา
                        </Button>
                      </Box>
                    </Paper>
                  </Box>
                </Fade>
              )}
            </Paper>
          </Fade>
        </Container>

        {/* ไม่เจอพนักงาน — แยก Container เหมือน ManpowerCheckin */}
        {!openScan && employee?.rows?.length === 0 && (
          <Container maxWidth="sm" sx={{ mb: 4 }}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 4, textAlign: 'center', bgcolor: '#f9f9f9' }}>
              <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                ไม่มีข้อมูลพนักงาน
              </Typography>
              <IconButton
                onClick={() => { setOpenScan(true); setEmInput(''); dispatch(actions.employeeReset()); }}
                sx={{ color: '#f44336', bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' } }}
              >
                <ClearIcon fontSize="large" />
              </IconButton>
            </Paper>
          </Container>
        )}
      </Box>
    );
  }

  if (success) {
    return (
      <Box className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 flex items-center justify-center">
        <Container maxWidth="sm">
          <Paper
            elevation={4}
            sx={{
              p: 4,
              borderRadius: 4,
              textAlign: 'center',
              background: 'linear-gradient(to bottom, #ffffff, #f5f8ff)',
              boxShadow: '0 8px 24px rgba(149, 157, 165, 0.2)',
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 80, color: '#43a047', mb: 2 }} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#43a047', mb: 1 }}>
              ส่งคำขอ OT สำเร็จ
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              คำขอ OT ถูกส่งเรียบร้อยแล้ว
            </Typography>
            <Button
              variant="outlined"
              onClick={() => window.history.back()}
              sx={{ borderRadius: 2 }}
            >
              กลับ
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100">
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Header */}
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Fade in={true}>
            <Paper
              elevation={4}
              sx={{
                p: 3,
                borderRadius: 4,
                background: 'linear-gradient(to bottom, #ffffff, #f5f8ff)',
                boxShadow: '0 8px 24px rgba(149, 157, 165, 0.2)',
              }}
            >
              <Typography
                variant="h4"
                className="font-bold text-center mb-2"
                sx={{ color: '#1976d2' }}
              >
                ขอทำโอที
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <Box className="flex justify-center mb-2">
                <Avatar sx={{ bgcolor: '#e3f2fd', width: 56, height: 56 }}>
                  <AlarmAddIcon fontSize="large" color="primary" />
                </Avatar>
              </Box>

              <Typography variant="h6" className="text-center mb-1" sx={{ color: '#1976d2' }}>
                โครงการ {project?.name}
              </Typography>
              <Typography variant="body2" className="text-center mb-1" color="text.secondary">
                ผู้ทำรายการ: <b>{formatEmployeeThaiName(requester)}</b>
                <IconButton size="small" onClick={() => { setRequester(null); setOpenScan(true); setEmInput(''); dispatch(actions.employeeReset()); }} sx={{ ml: 1 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Typography>
              <Typography variant="h5" className="text-center mb-4 font-bold" sx={{ color: '#333' }}>
                {currentTime}
              </Typography>

              <Fade in={true}>
                <Box>
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
                    <Grid container spacing={2}>

                      {/* ขอโอทีย้อนหลัง */}
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            p: 2,
                            bgcolor: alpha(theme.palette.warning.main, 0.08),
                            borderRadius: 2,
                            border: `1px dashed ${theme.palette.warning.main}`,
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={claimOT}
                                onChange={(e) => setClaimOT(e.target.checked)}
                                color="warning"
                              />
                            }
                            label={
                              <Typography variant="body2" fontWeight={500}>
                                ขอโอทีย้อนหลัง
                              </Typography>
                            }
                          />
                        </Box>
                      </Grid>

                      {/* วันที่และเวลา */}
                      <Grid item xs={12}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                          <Box className="flex items-center mb-2">
                            <CalendarTodayIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="subtitle1" fontWeight={600}>
                              วันที่และเวลา
                            </Typography>
                          </Box>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Controller
                                name="date"
                                control={control}
                                defaultValue={dayjs()}
                                render={({ field }) => (
                                  <DatePicker
                                    label="วันที่ขอ OT"
                                    value={field.value}
                                    onChange={(e) => { field.onChange(e); setDate(e); }}
                                    renderInput={(params) => (
                                      <TextField {...params} fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                                    )}
                                    minDate={claimOT ? undefined : dayjs()}
                                    maxDate={dayjs()}
                                    disableFuture={!claimOT}
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <Controller
                                name="startTime"
                                control={control}
                                defaultValue={dayjs().hour(17).minute(0)}
                                render={({ field }) => (
                                  <TextField
                                    select
                                    label="เวลาเริ่ม OT"
                                    fullWidth
                                    value={field.value ? dayjs(field.value).format('HH:mm') : ''}
                                    onChange={(e) => field.onChange(toDayjsTime(e.target.value))}
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                  >
                                    {timeOptions.map((t) => (
                                      <MenuItem key={t} value={t}>{t}</MenuItem>
                                    ))}
                                  </TextField>
                                )}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <Controller
                                name="endTime"
                                control={control}
                                defaultValue={dayjs().hour(20).minute(0)}
                                render={({ field }) => (
                                  <TextField
                                    select
                                    label="เวลาสิ้นสุด OT"
                                    fullWidth
                                    value={field.value ? dayjs(field.value).format('HH:mm') : ''}
                                    onChange={(e) => field.onChange(toDayjsTime(e.target.value))}
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                  >
                                    {timeOptions.map((t) => (
                                      <MenuItem key={t} value={t}>{t}</MenuItem>
                                    ))}
                                  </TextField>
                                )}
                              />
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>

                      {/* เลือกพนักงาน */}
                      <Grid item xs={12}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                          <Box className="flex items-center mb-2">
                            <GroupsIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="subtitle1" fontWeight={600}>
                              พนักงาน
                            </Typography>
                          </Box>
                          <Badge badgeContent={selectedEmployees.length || 0} color="primary" max={999}>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<GroupsIcon />}
                              onClick={() => setOpenEmployeeModal(true)}
                              sx={{ borderRadius: 2 }}
                            >
                              เลือกพนักงาน
                            </Button>
                          </Badge>

                          {selectedEmployees.length > 0 && (
                            <List sx={{ py: 0, mt: 1 }}>
                              {selectedEmployees.map((emp, index) => (
                                <React.Fragment key={emp.employee_id}>
                                  <ListItem sx={{ py: 1, px: 0 }}>
                                    <ListItemAvatar>
                                      <Avatar sx={{ width: 36, height: 36, bgcolor: '#1976d2' }}>
                                        <PersonIcon fontSize="small" />
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary={
                                        <Typography variant="body2" fontWeight={500}>
                                          {emp.name}
                                        </Typography>
                                      }
                                      secondary={
                                        emp.checkIn && (
                                          <Stack direction="row" alignItems="center" spacing={0.5}>
                                            <AccessTimeIcon sx={{ fontSize: 12 }} />
                                            <Typography variant="caption" color="text.secondary">
                                              เข้างาน {dayjs(emp.checkIn).format('HH:mm น.')}
                                            </Typography>
                                          </Stack>
                                        )
                                      }
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        setSelectedEmployees(selectedEmployees.filter((e) => e.employee_id !== emp.employee_id))
                                      }
                                      sx={{ color: '#f44336', bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' } }}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </ListItem>
                                  {index < selectedEmployees.length - 1 && <Divider />}
                                </React.Fragment>
                              ))}
                            </List>
                          )}
                        </Paper>
                      </Grid>

                      {/* เหตุผล */}
                      <Grid item xs={12}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                          <Box className="flex items-center mb-2">
                            <DescriptionIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="subtitle1" fontWeight={600}>
                              เหตุผลในการขอ OT
                            </Typography>
                          </Box>
                          <Controller
                            name="description"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                multiline
                                rows={3}
                                required
                                size="small"
                                placeholder="เช่น งานเร่งด่วน, ปิดโปรเจค, ลูกค้าต้องการด่วน"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                              />
                            )}
                          />
                        </Paper>
                      </Grid>

                      {/* Submit */}
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                          startIcon={<CheckCircleIcon />}
                          sx={{
                            borderRadius: 3,
                            py: 1,
                            px: 4,
                            boxShadow: 2,
                            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                          }}
                        >
                          ส่งคำขอ OT
                        </Button>
                      </Grid>

                    </Grid>
                  </LocalizationProvider>
                </Box>
              </Fade>
            </Paper>
          </Fade>
        </Container>
      </form>

      {/* Modal เลือกพนักงาน */}
      <Dialog
        open={openEmployeeModal}
        onClose={() => setOpenEmployeeModal(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 3, maxHeight: isMobile ? '100vh' : '80vh' },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#1976d2',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2,
          }}
        >
          <Box display="flex" alignItems="center">
            <GroupsIcon sx={{ mr: 1 }} />
            <Typography variant="h6" fontWeight="bold">
              เลือกพนักงาน - {dayjs(date).format('DD/MM/YYYY')}
            </Typography>
          </Box>
          <IconButton onClick={() => setOpenEmployeeModal(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: '#f5f5f5' }}>
            <FormControlLabel
              label={
                <Typography variant="body2" fontWeight={500}>
                  เลือกทั้งหมด ({employeeOptions.length} คน)
                </Typography>
              }
              control={
                <Checkbox
                  checked={employeeOptions.length > 0 && selectedEmployees.length === employeeOptions.length}
                  indeterminate={selectedEmployees.length > 0 && selectedEmployees.length < employeeOptions.length}
                  onChange={(e) => setSelectedEmployees(e.target.checked ? [...employeeOptions] : [])}
                />
              }
            />
          </Box>

          {employeeOptions.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <GroupsIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
              <Typography variant="h6">ไม่พบพนักงานที่ลงเวลาวันนี้</Typography>
              <Typography variant="body2">ข้อมูลจะแสดงเมื่อมีพนักงานลงเวลาเข้างาน</Typography>
            </Box>
          ) : (
            <List sx={{ py: 0 }}>
              {employeeOptions.map((emp, index) => (
                <React.Fragment key={emp.employee_id}>
                  <ListItem
                    sx={{ py: 2, px: 3, '&:hover': { bgcolor: '#f5f5f5' }, cursor: 'pointer' }}
                    onClick={() => {
                      const isSelected = selectedEmployees.some((e) => e.employee_id === emp.employee_id);
                      setSelectedEmployees(
                        isSelected
                          ? selectedEmployees.filter((e) => e.employee_id !== emp.employee_id)
                          : [...selectedEmployees, emp],
                      );
                    }}
                  >
                    <Checkbox
                      checked={selectedEmployees.some((e) => e.employee_id === emp.employee_id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => {
                        const isSelected = selectedEmployees.some((e) => e.employee_id === emp.employee_id);
                        setSelectedEmployees(
                          isSelected
                            ? selectedEmployees.filter((e) => e.employee_id !== emp.employee_id)
                            : [...selectedEmployees, emp],
                        );
                      }}
                    />
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          width: isMobile ? 48 : 56,
                          height: isMobile ? 48 : 56,
                          border: '2px solid #1976d2',
                        }}
                      >
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" fontWeight="bold">
                          {emp.name}
                        </Typography>
                      }
                      secondary={
                        emp.checkIn && (
                          <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                            <AccessTimeIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              เข้างาน {dayjs(emp.checkIn).format('HH:mm น.')}
                            </Typography>
                          </Box>
                        )
                      }
                    />
                  </ListItem>
                  {index < employeeOptions.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}

          <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              เลือกแล้ว <strong>{selectedEmployees.length}</strong> / {employeeOptions.length} คน
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenEmployeeModal(false)} variant="contained" sx={{ borderRadius: 2 }}>
            ตกลง
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
